import { useEffect, useState, useCallback } from 'react'
import { AlertTriangle, Download, Plus, Loader2, ShieldCheck } from 'lucide-react'
import toastService from '@/services/toast/service'
import { AgencyCombobox } from '@/components/AgencyCombobox'
import { TableSkeleton } from '@/components/skeletons'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { xrayAdminService, type AdminXrayComplaint } from '@/services/xrayAdmin'

/**
 * X-Ray compliance desk: the complaint and breach queue, and the evidence
 * exports the terms oblige us to produce on request.
 *
 * Backoffice only, on purpose. A complaint about an agency must never be
 * visible to that agency, and an export is somebody's audit response rather
 * than a customer feature.
 */

const SOURCES = [
  { value: 'consumer', label: 'Consumer' },
  { value: 'regulator', label: 'Regulator' },
  { value: 'customer_forwarded', label: 'Forwarded by customer' },
  { value: 'data_provider', label: 'Data provider' },
  { value: 'breach', label: 'Breach report (72h clock)' },
]

const STATUSES = ['open', 'investigating', 'forwarded', 'resolved']

/** Lowercase in the database, title case everywhere a person reads it. */
const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  investigating: 'Investigating',
  forwarded: 'Forwarded',
  resolved: 'Resolved',
}

const SOURCE_LABEL: Record<string, string> = Object.fromEntries(
  SOURCES.map((s) => [s.value, s.label]),
)

/** How long it has been waiting, in the unit its own clock uses. */
function elapsedLabel(c: { hoursElapsed: number | null; businessDaysElapsed: number }): string {
  if (c.hoursElapsed != null) return `${c.hoursElapsed}h`
  if (c.businessDaysElapsed === 0) return 'Today'
  return `${c.businessDaysElapsed} business day${c.businessDaysElapsed === 1 ? '' : 's'}`
}

/** Received, Source, Agency, Summary, Elapsed, Status. */
const QUEUE_COLUMNS = 6

const EXPORT_KINDS = [
  {
    value: 'acceptances',
    label: 'Acceptance records',
    detail: 'Who accepted the X-Ray terms, their role and IP, and the hash of the exact text they saw.',
  },
  {
    value: 'consent',
    label: 'Consent evidence',
    detail: 'Every consumer consent: how it was given, the GPC signal, whether the notice was opened, and the IP.',
  },
  {
    value: 'suppressions',
    label: 'Suppression log',
    detail: 'Opt-outs recorded through Your Privacy Choices, as hashed identifiers.',
  },
] as const

export default function XrayCompliance() {
  const [complaints, setComplaints] = useState<AdminXrayComplaint[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')

  const [draft, setDraft] = useState({ source: 'consumer', summary: '', consumerIdentifier: '' })
  const [isSaving, setIsSaving] = useState(false)

  const [exportAgencyPublicId, setExportAgencyPublicId] = useState('')
  const [exportingKind, setExportingKind] = useState<string | null>(null)
  const [recordOpen, setRecordOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setComplaints(await xrayAdminService.getComplaints(statusFilter === 'all' ? undefined : statusFilter))
    } catch {
      toastService.error("Couldn't load complaints")
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    void load()
  }, [load])

  const handleRecord = async () => {
    if (!draft.summary.trim() || isSaving) return
    setIsSaving(true)
    try {
      await xrayAdminService.createComplaint({
        source: draft.source,
        summary: draft.summary.trim(),
        consumerIdentifier: draft.consumerIdentifier.trim() || undefined,
      })
      toastService.success('Complaint recorded')
      setDraft({ source: 'consumer', summary: '', consumerIdentifier: '' })
      setRecordOpen(false)
      await load()
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || "Couldn't record complaint")
    } finally {
      setIsSaving(false)
    }
  }

  const handleStatus = async (publicId: string, status: string) => {
    try {
      await xrayAdminService.updateComplaint(publicId, { status })
      await load()
    } catch {
      toastService.error("Couldn't update complaint")
    }
  }

  const handleExport = async (kind: (typeof EXPORT_KINDS)[number]['value']) => {
    if (!exportAgencyPublicId) {
      toastService.error('Select an agency')
      return
    }
    setExportingKind(kind)
    try {
      const result = await xrayAdminService.downloadExport({
        agencyPublicId: exportAgencyPublicId,
        kind,
      })
      toastService.success(`Exported ${result.records} records`)
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || "Couldn't produce export")
    } finally {
      setExportingKind(null)
    }
  }

  const overdue = complaints.filter((c) => c.forwardOverdue).length

  return (
    <div className="space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0 pb-24">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">X-Ray Compliance</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Consumer complaints, regulatory inquiries and breach reports, plus the evidence
          exports we are obliged to produce on request.
        </p>
      </div>

      {overdue > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-300 bg-red-50/70 dark:border-red-900/40 dark:bg-red-950/20 px-3.5 py-3 text-[13px]">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-600" />
          <span>
            <strong>{overdue}</strong> {overdue === 1 ? 'item is' : 'items are'} past the
            deadline. A complaint must be forwarded within five business days; a breach must
            reach LeadFi within 72 hours of discovery.
          </span>
        </div>
      )}

      <Tabs defaultValue="complaints" className="w-full">
        <TabsList>
          <TabsTrigger value="complaints">Complaints</TabsTrigger>
          <TabsTrigger value="export">Evidence export</TabsTrigger>
        </TabsList>

        <TabsContent value="complaints" className="mt-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="text-base">Complaints and breaches</CardTitle>
            <CardDescription className="mt-1">
              Anything raised with us, and how long it has been waiting.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABEL[s] ?? s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setRecordOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Record
          </Button>
          </div>
        </CardHeader>
        <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Received</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Elapsed</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableSkeleton columns={QUEUE_COLUMNS} rows={5} />
                ) : complaints.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={QUEUE_COLUMNS} className="h-48 text-center text-muted-foreground">
                      <div className="flex flex-col items-center justify-center">
                        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                          <ShieldCheck className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <p className="font-medium text-foreground">Nothing recorded</p>
                        <p className="text-sm mt-1">
                          Complaints and breach reports appear here once recorded.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  complaints.map((c) => (
                  <TableRow key={c.publicId} className={c.forwardOverdue ? 'bg-red-50/50 dark:bg-red-950/10' : undefined}>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums">
                      {new Date(c.receivedAtUtc).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.source === 'breach' ? (
                        <Badge variant="destructive">Breach</Badge>
                      ) : (
                        <Badge variant="secondary" className="font-medium text-xs shadow-none cursor-default">
                          {SOURCE_LABEL[c.source] ?? c.source.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.agency?.name ?? <span className="text-muted-foreground">Unattributed</span>}
                    </TableCell>
                    <TableCell className="text-sm max-w-[320px] truncate" title={c.summary}>
                      {c.summary}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap tabular-nums">
                      {elapsedLabel(c)}
                      {c.forwardOverdue && (
                        <span className="ml-1.5 font-semibold text-red-600">overdue</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Select value={c.status} onValueChange={(v) => handleStatus(c.publicId, v)}>
                        <SelectTrigger className="w-[150px] h-8 text-sm ml-auto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {STATUS_LABEL[s] ?? s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="export" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Evidence export</CardTitle>
              <CardDescription className="mt-1">
                What we are obliged to produce on written request, within ten business days
                (xray-terms 11.2). NDJSON, one record per line.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 max-w-[320px]">
                <Label htmlFor="xc-agency">Agency</Label>
                <AgencyCombobox
                  id="xc-agency"
                  value={exportAgencyPublicId}
                  onChange={setExportAgencyPublicId}
                />
              </div>

              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Records</TableHead>
                      <TableHead className="w-[110px] text-right">Export</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {EXPORT_KINDS.map((k) => (
                      <TableRow key={k.value}>
                        <TableCell className="py-3">
                          <div className="text-sm font-medium">{k.label}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{k.detail}</div>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExport(k.value)}
                            disabled={!exportAgencyPublicId || exportingKind !== null}
                          >
                            {exportingKind === k.value ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="h-4 w-4 mr-1.5" />
                                Export
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {!exportAgencyPublicId && (
                <p className="text-xs text-muted-foreground">
                  Choose an agency to enable the exports.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Record a complaint or breach</DialogTitle>
            <DialogDescription>
              The clock runs from when it reached us, so record it as soon as it arrives.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="xc-source">Source</Label>
              <Select value={draft.source} onValueChange={(v) => setDraft((d) => ({ ...d, source: v }))}>
                <SelectTrigger id="xc-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {SOURCES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="xc-identifier">Who it is about</Label>
              <Input
                id="xc-identifier"
                value={draft.consumerIdentifier}
                onChange={(e) => setDraft((d) => ({ ...d, consumerIdentifier: e.target.value }))}
                placeholder="Email, phone, or however they identified themselves"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="xc-summary">What they said</Label>
            <Textarea
              id="xc-summary"
              value={draft.summary}
              onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
              rows={3}
              placeholder="Summarise the complaint or the breach as reported"
            />
          </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleRecord} disabled={isSaving || !draft.summary.trim()}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}
